/** @jsxImportSource theme-ui */
import { jsx } from "theme-ui";
import React, { useState } from "react";
import { Flex, Box, Link, Image, Text } from "@theme-ui/components";
import { useResponsiveValue } from "@theme-ui/match-media";
import { Link as InternalLink } from "gatsby";
import { useSelector } from "react-redux";
import { StaticImage } from "gatsby-plugin-image";
import moment from "moment";
import footer from "../../data/footer.yml";
import meFooter from "../../data/me_footer.yml";
import idFooter from "../../data/id_footer.yml";
import idBahasaFooter from "../../data/id_bahasa_footer.yml";
import { Accordion, SocialHandles } from "../organisms";
import { CustomLink, Icon, RegionalLink } from '../atoms';
import { colors, typography } from '../../styles/theme';
import greythrLogo from "../../images/greythr-logo.svg";
import whatsAppLogo from "../../images/whatsapp-logo.svg";  
import gdpr from "../../images/new-gdpr.png"
import soc from "../../images/soc2.png"
import iso from "../../images/iso.png"
import playStore from "../../images/play-store.png";
import appStore from "../../images/app-store.png"
import { getCommonData } from "../../../../shared-utils/src/components/utils/regionContent";

const FooterLink = (props) => {
  const { path, title, region, regional = false, customStyles } = props;
  const sx = {
    ...typography.textMd400,
    color: colors.neutral900,
    "&:hover": {
      textDecoration: ["none", "none", "underline"],
      color: colors.primaryMain,
    },
    ...customStyles,
  };
  return path.split("/")[0] === "https:" ? (
    <Link sx={sx} href={path} target="_blank" rel="noopener">
      <Text as="span">{title}</Text>
    </Link>
  ) : (
    <RegionalLink
      className="flink"
      sx={sx}
      to={path}
      region={region}
      regional={regional}
    >
      <Text as="span">{title}</Text>
    </RegionalLink>
  );
};

const Certifications = ({ region }) => {
  const blogLink =
    region === "ME"
      ? "/middle-east/blog/greytip-software-is-now-iso-certified/"
      : "/blog/greytip-software-is-now-iso-certified/";
  const whatsAppLink = "https://wa.me/919606021119";
  const whatsAppLinkId = "https://api.whatsapp.com/send/?phone=6285716726490";
  const data = getCommonData(region)?.footer;
  const officeAddress =
  region === "IN"
    ? "Grape Garden, #29 & 30, 17th Main, 6th Block, Koramangala, Bengaluru - 560095"
    : "Thrive Colony Indonesia Unit 9F, Lantai 9, Cyber 2 Tower, Jl. H. R. Rasuna Said No. 13, Kuningan, Kota Jakarta Selatan, 12950.";
  return (
    <Flex sx={styles.certifications}>
      <Flex sx={styles.imagesContainer}>
        <Image
          src={greythrLogo}
          alt="greytHR-logo"
          sx={{ width: ["6.438rem", "6.438rem", "11.25rem"] }}
        />
        <Flex
          sx={{
            gap: [3, 3, "1.5rem"],
            height: "100%",
          }}
        >
          <Image
            src={gdpr}
            alt="GDPR Compliant certification badge"
            sx={styles.circleLogos}
          />
          <Image
            src={soc}
            alt="Soc2 certification badge"
            sx={styles.circleLogos}
          />
          <InternalLink to={blogLink}>
            <Image src={iso} alt="ISO Certification Badge" sx={styles.iso} />
          </InternalLink>
        </Flex>
      </Flex>
      {["IN", "ID", "ID-id"].includes(region) && (
        <Flex sx={{ flexDirection: "column", flexShrink: 1 }}>
          <Link
            href={region === "IN" ? whatsAppLink : whatsAppLinkId}
            aria-label={data?.whatsappText}
            target="_blank"
            rel="noopener"
            sx={styles.whatsapp}
          >
            <Flex
              sx={{
                gap: "0.75rem",
                alignItems: "center",
                p: "0.75rem",
                minWidth: "16.75rem",
              }}
            >
              <Image
                src={whatsAppLogo}
                alt="WhatsApp Logo"
                style={{ width: "1.5rem" }}
              />
              <Text>
                {data?.whatsappText}
              </Text>
            </Flex>
          </Link>
          <Flex
            sx={{
              maxWidth: "22.188rem",
              mt: ["1rem", "1rem", "1rem", "0.5rem"],
            }}
          >
            <Flex sx={{ flexShrink: 0, mx: "0.75rem" }}>
              <Icon name="MapPin" color={colors.primaryMain} size={24} />
            </Flex>
            <Box sx={{ ...typography.textMd400, color: colors.neutral900 }}>
              {officeAddress}
            </Box>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

const AppLinks = () => {
  return (
    <Flex sx={styles.appLinks}>
      <CustomLink url="https://bit.ly/greytHRapp">
        <img
          src={playStore}
          alt="PlayStore"
          sx={styles.storeLinks}
        />
      </CustomLink>
      <CustomLink url="https://cutt.ly/greytHRapp">
        <img
          src={appStore}
          alt="AppStore"
          sx={styles.storeLinks}
        />
      </CustomLink>
    </Flex>
  );
};

const DesktopFooterItems = ({ data, region }) => {
  return (
    <Flex sx={styles.footerItems}>
      {Object.entries(data).map(([key, value]) => {
        const { name, items } = value;
        if (name === "Competitors") return;
        return (
          <>
            <ul key={key} sx={styles.itemList}>
              <li sx={{ ...typography.h5, mb: "1.438rem" }}>{name}</li>
              {items?.map(({ title, path, regional }) => (
                <li key={title}>
                  <FooterLink
                    path={path}
                    region={region}
                    regional={regional}
                    title={title}
                  />
                </li>
              ))}
              {name === "greytHR App" || name === "greytHR Customers" || name === "Aplikasi greytHR" ? (
                <li key="app-links">
                  <AppLinks />
                </li>
              ) : null}
            </ul>
          </>
        );
      })}
    </Flex>
  );
};

const MobileFooterItems = ({ data, region }) => {
  return (
    <Box>
      {data &&
        Object.entries(data).map(([key, value]) => {
          const { name, items } = value;
          if (name === "greytHR App") return null;
          return (
            <Box key={key} sx={styles.accordion}>
              <Accordion>
                <div
                  style={{ px: 0, my: 0, pb: 0 }}
                  label={name}
                  openIconName="ChevronRight"
                  closeIconName="ChevronRight"
                  iconSize={20}
                  showChevronIcon
                >
                  {items?.map(
                    ({ title, path, regional }) =>
                      title !== "Contact us" && (
                        <div sx={styles.mobileFooterItems} key={title}>
                          <FooterLink
                            path={path}
                            region={region}
                            regional={regional}
                            title={title}
                          />
                        </div>
                      )
                  )}
                </div>
              </Accordion>
            </Box>
          );
        })}
      {!(region === "ID" || region === "SG" || region === "MY") && (
        <Box p={3} mt={3}>
          <FooterLink
            path={"/contact-us/"}
            region={region}
            regional={true}
            title={"Contact us"}
            customStyles={{
              ...typography.textMd500,
            }}
          />
        </Box>
      )}
      <AppLinks />
    </Box>
  );
};

const Footer = ({ region }) => {
  const currentYear = moment().year();
  var footerData = footer;
  var pageRegionCode = region;
  if (!region) {
    //****Getting Region from date when it is not passed.*/
    pageRegionCode = useSelector((state) => state.app.region);
  }
  const footerMapping = {
    IN: footer,
    ME: meFooter,
    ID: idFooter,
    "ID-id": idBahasaFooter,
  };
  footerData = footerMapping[pageRegionCode] || footer;

  const footerItems = useResponsiveValue(
    [
      <MobileFooterItems data={footerData} region={pageRegionCode} />,
      <MobileFooterItems data={footerData} region={pageRegionCode} />,
      <DesktopFooterItems data={footerData} region={pageRegionCode} />,
    ],
    {
      defaultIndex: 2,
    }
  );

  return (
    <footer sx={styles.container}>
      {!["SG", "MY"].includes(pageRegionCode) && footerItems}
      <Certifications region={pageRegionCode} />
      <Flex sx={styles.lastRow}>
        <Flex sx={styles.bottomLinks}>
          <Box>© {currentYear} Greytip Software Pvt. Ltd.</Box>
          <InternalLink to="/privacy-policy/">Privacy Policy</InternalLink>
          <InternalLink to="/terms-service/">Terms of Use</InternalLink>
        </Flex>
        <Box
          sx={{
            order: 3,
            "@media(max-width: 1077px)": {
              order: 2,
            },
          }}
        >
          <SocialHandles region={pageRegionCode} />
        </Box>
      </Flex>
    </footer>
  );
};

const styles = {
  container: {
    bg: colors.white,
    py: [5, 5, "7rem"],
    px: [3, 3, "3rem", 0],
    width: ["100%", "100%", "100%", "1346px"],
    mx: "auto",
    "@media(max-width: 1420px)": {
      width: "90%",
    },
    "@media(max-width: 1023px)": {
      width: "100%",
      px: [3, 3, "3rem"],
    },
  },
  circleLogos: {
    width: ["3rem", "3rem", "3.438rem", "3.438rem"],
    height: ["3rem", "3rem", "3.438rem", "3.438rem"],
  },
  iso: {
    width: ["5.875rem", "5.875rem", "6.75rem", "6.75rem"],
    height: ["3rem", "3rem", "3.438rem", "3.438rem"],
  },
  bottomLinks: {
    gap: "1.5rem",
    alignItems: "center",
    ...typography.textSm,
    order: [3, 3, 3, 2],
    color: colors.neutral900,
    flexWrap: ["wrap", "wrap", "no-wrap"],
    justifyContent: ["center", "center", "center", "flex-start"],
    "@media(max-width: 1077px)": {
      justifyContent: "center",
      order: 3,
    },
    a: {
      color: colors.neutral900,
      textDecoration: "none",
      "&:hover": {
        color: colors.primaryMain,
        textDecoration: "underline",
      },
    },
  },
  lastRow: {
    justifyContent: ["center", "center", "center", "space-between"],
    alignItems: "center",
    borderTop: `1px solid ${colors.neutral200}`,
    pt: [4, 4, "1.5rem", "1.25rem"],
    gap: [4, 4, "1.5rem"],
    flexDirection: ["column", "column", "column", "row"],
    "@media(max-width: 1077px)": {
      justifyContent: "center",
      flexDirection: "column",
      pt: [4, 4, "1.5rem"],
    },
  },
  footerItems: {
    flexWrap: "nowrap",
    justifyContent: "center",
    gap: 5,
    "@media(max-width: 1023px)": {
      flexWrap: "wrap",
      justifyContent: "flex-start",
    },
  },
  itemList: {
    m: 0,
    p: 0,
    listStyleType: "none",
    display: "flex",
    flexDirection: "column",
    gap: "0.813rem",
  },
  accordion: {
    ".accordion-label": {
      ...typography.textMd500,
      mr: 3,
    },
    ".accordion-heading": {
      p: 3,
    },
    ".accordion-heading.close": {
      ".accordion-label": {
        color: colors.black,
      },
      svg: {
        stroke: colors.black,
      },
    },
    ".accordion-heading.open": {
      ".accordion-label": {
        color: colors.primaryMain,
      },
      svg: {
        stroke: colors.primaryMain,
      },
    },
    ".accordion-section": {
      py: 3,
      borderBottom: `1px solid ${colors.neutral200}`,
    },
  },
  appLinks: {
    mt: [4, 4, 0],
    gap: "1.5rem",
    maxWidth: ["100%", "100%", "11.938rem"],
    flexDirection: ["row", "row", "column"],
    justifyContent: ["center", "center", "flex-start"],
    alignItems: ["center", "center", "flex-start"],
  },
  mobileFooterItems: {
    p: 3,
    ...typography.textMd400,
    color: colors.neutral900,
    "&:hover": {
      bg: [colors.primary100, colors.primary100, "transparent"],
    },
  },
  certifications: {
    justifyContent: ["center", "center", "space-between", "space-between"],
    alignItems: "center",
    flexDirection: ["column", "column", "row", "row"],
    py: [4, 4, "3rem", "1.5rem"],
    "@media (max-width: 734px)": {
      flexDirection: "column",
      gap: "2rem",
    },
  },
  imagesContainer: {
    gap: ["2rem", "2rem", "2rem", "3rem"],
    flexDirection: ["row", "row", "column", "row"],
    flexShrink: 0,
    "@media (min-width: 900px)": {
      justifyContent: "center",
      alignItems: "center",
    },
    "@media (max-width: 734px)": {
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
    },
  },
  storeLinks: {
    width: "11.938rem",
    "@media(max-width: 599px)": {
      width: "9.563rem",
    },
  },
  whatsapp: {
    ...typography.textMd400,
    color: colors.neutral900,
    "&:hover": {
      textDecoration: "underline",
      color: colors.primaryMain,
    },
  },
};

export default Footer;
